import React from "react";
import { MessageCircle, Download } from "lucide-react";
import PageHero from "@/components/PageHero";
import SectionLabel from "@/components/SectionLabel";
import ProductCard from "@/components/ProductCard";
import SafeImage from "@/components/SafeImage";
import { SOLUTIONS } from "@/data/solutions";
import { PRODUCTS } from "@/data/products";
import { buildWaLink, buildSolutionWaLink } from "@/lib/whatsapp";
import { BAH_PRODUCT_CATALOGUE_PDF } from "@/lib/assets";

function productsForSolution(solution) {
  return PRODUCTS.filter((p) => solution.products.includes(p.name));
}

export default function Solutions() {
  return (
    <div data-testid="solutions-page">
      <PageHero
        testId="solutions-hero"
        eyebrow="Animal Health Solutions"
        title="Organised by what professionals actually need."
        lede="Nine practical solution areas — combining medicines, instruments and cold-chain into a coherent animal health platform for East Africa."
        image="https://images.pexels.com/photos/422218/pexels-photo-422218.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1100&w=1600"
        primaryCta={{
          label: "WhatsApp Technical Team",
          href: buildWaLink("Hello Biodeal Animal Health, I'd like guidance on selecting solutions for my farm or practice."),
          external: true,
        }}
        secondaryCta={{ label: "Use Product Finder", to: "/vet-support-hub#hub-product-finder" }}
      />

      <section className="py-16 md:py-24 bg-bah-paper">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-20">
          {SOLUTIONS.map((s, idx) => {
            const sProducts = productsForSolution(s);
            return (
              <article
                key={s.id}
                id={s.id}
                className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start scroll-mt-28"
                data-testid={`solution-section-${s.id}`}
              >
                <div className={`lg:col-span-5 ${idx % 2 === 1 ? "lg:order-2" : ""}`}>
                  <div className="relative aspect-[4/3] overflow-hidden rounded-2xl border border-bah-line">
                    <SafeImage
                      src={s.image}
                      alt={s.title}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-bah-ink/70 to-transparent" />
                  </div>
                </div>
                <div className={`lg:col-span-7 ${idx % 2 === 1 ? "lg:order-1" : ""}`}>
                  <SectionLabel>Solution {String(idx + 1).padStart(2, "0")}</SectionLabel>
                  <h2 className="mt-4 font-display text-3xl md:text-4xl text-bah-ink leading-tight">
                    {s.title}
                  </h2>
                  <p className="mt-4 text-bah-mute md:text-lg max-w-2xl">{s.blurb}</p>

                  {sProducts.length > 0 ? (
                    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                      {sProducts.map((p) => (
                        <ProductCard key={p.id} product={p} />
                      ))}
                    </div>
                  ) : (
                    <div className="mt-6 rounded-xl border border-bah-line bg-white p-5">
                      <div className="text-sm font-semibold text-bah-ink">Featured items in this solution:</div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {s.products.map((p) => (
                          <span
                            key={p}
                            className="text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full bg-bah-paperAlt border border-bah-line text-bah-ink"
                          >
                            {p}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mt-8 flex flex-wrap gap-3">
                    <a
                      href={buildSolutionWaLink(s.title)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-whatsapp"
                      data-testid={`solution-cta-whatsapp-${s.id}`}
                    >
                      <MessageCircle className="h-4 w-4" /> Talk to Technical Team
                    </a>
                    <a
                      href={BAH_PRODUCT_CATALOGUE_PDF}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-ghost-dark"
                      data-testid={`solution-cta-catalogue-${s.id}`}
                    >
                      <Download className="h-4 w-4" /> Download Catalogue
                    </a>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
}
